import { z } from "zod";

import { SupportedCountry } from "@/constants/country";
import { PRODUCT_PERMISSION_SCOPE_CODES } from "@/features/permission-and-role/constants/scope-code";
import type { SupportedLanguage } from "@/lib/i18n";

import type { AccessToken, IdToken } from "./types";

// JWTトークンの形式を表す正規表現
const JWT_REGEX = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

// AccessTokenとIdTokenのSchema
export const tokenSchema = z.string().min(1).regex(JWT_REGEX);

// UserSessionのSchema
export const userSessionSchema = z.object({
  /**
   * @see https://standandforce.slack.com/archives/C075SH8PNPR/p1732694875732869?thread_ts=1732665610.964199&cid=C075SH8PNPR
   * @note 未対応のlocaleが指定された場合、en-USにフォールバック
   */
  hasServiceAccount: z.boolean(),
  hasOperator: z.boolean(),
  hasIndividual: z.boolean(),
  /**
   * 以下のようにしてしまうと、バックエンドで返却される Code が増えた時に Zod の Validation エラーになってしまう
   * hasPermissionScopes: z.array(z.nativeEnum(PRODUCT_PERMISSION_SCOPE_CODES)),
   */
  hasPermissionScopes: z.preprocess(
    (input) =>
      Array.isArray(input)
        ? input.filter((val) =>
            Object.values(PRODUCT_PERMISSION_SCOPE_CODES).includes(val),
          )
        : [],
    z.array(z.nativeEnum(PRODUCT_PERMISSION_SCOPE_CODES)),
  ),
  validTo: z.string().nullish(), // 🐕
  locale: z
    .custom<SupportedLanguage>()
    .catch("en-US" as const)
    .default("en-US" as const),
  user: z.object({
    id: z.string(),
    cognitoSub: z.string(),
    email: z.string(),
  }),
  organization: z.object({
    tenantId: z.string(),
    organizationId: z.string(),
    primaryLocale: z
      .custom<SupportedLanguage>()
      .catch("en-US" as const)
      .default("en-US" as const),
    tenantName: z.string(),
    organizationName: z.string(),
    location: z.nativeEnum(SupportedCountry),
    locationFlagUrl: z.string(),
  }),
  switchableOrganizations: z.array(
    z.object({
      sessionType: z
        .enum(["MEMBERSHIP", "SERVICE_ACCOUNT"])
        // API 後方互換性のために optional 指定。
        // sessionType を返す backend がリリースされた後は🐕全て消してOK
        .default("MEMBERSHIP"), // 🐕
      tenantId: z.string(),
      organizationId: z.string(),
      tenantName: z.string(),
      organizationName: z.string(),

      isDifferentTenant: z.boolean().optional(), // 🐕
      personName: z.string().optional(), // 🐕
      workerPrimaryEmail: z.string().optional(), // 🐕
    }),
  ),
  serviceAccounts: z.array(z.any()).optional(), // 🐕
  person: z.object({
    personId: z.string(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  }),
});

// AuthStoreのSchema
export const authStoreSchema = z.object({
  accessToken: z.custom<AccessToken>().nullable(),
  accessTokenExp: z.number().nullable(),
  idToken: z.custom<IdToken>().nullable(),
  idTokenExp: z.number().nullable(),
  session: userSessionSchema.nullable(),
  locale: z.custom<SupportedLanguage>().nullable(),
  setLocale: z
    .function(z.tuple([z.custom<SupportedLanguage>()]))
    .returns(z.void()),
  isAuthenticated: z.function().returns(z.promise(z.boolean())),
  refreshCognitoTokens: z.function().returns(
    z.promise(
      z.object({
        accessToken: tokenSchema,
        idToken: tokenSchema,
      }),
    ),
  ),
  refreshUserSession: z.function().returns(z.promise(userSessionSchema)),
  getAccessToken: z.function().returns(z.promise(tokenSchema)),
  getSession: z.function().returns(z.promise(userSessionSchema)),
  signIn: z
    .function()
    .args(
      z.object({
        username: z.string().min(1),
        password: z.string().min(1),
      }),
    )
    .returns(z.promise(z.any())),
  logout: z.function().returns(z.void()),
  downloadSignedCookies: z.function().returns(z.void()),
});
